import { FC, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Container } from '@/components/ui/Container';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';
import { authApi, inviteApi, cardApi, systemApi, ApiError } from '@/services/api';
import { User, ApiKeyDetail, CreateCardRequest, ChangePasswordRequest, AccountPermissions } from '@/types';
import { ContactManagement } from '@/components/ContactManagement';
import { CardCreationForm } from '@/components/CardCreationForm';
import { SystemSelector } from '@/components/SystemSelector';
import { PasswordChangeForm, PasswordChangeData } from '@/components/auth/PasswordChangeForm';
import { ApiKeyBindForm } from '@/components/ApiKeyBindForm';
import { AccountTypeSelector, ACCOUNT_PERMISSIONS_CONFIG } from '@/components/ui/AccountTypeSelector';
import { FAQManagement } from './FAQManagement';
import { AIAppManagement } from './AIAppManagement';


export const DashboardPage: FC = () => {
  const { user, logout, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview' | 'faq' | 'contact' | 'aiapp'>('overview');
  const [invitedUsers, setInvitedUsers] = useState<User[]>([]);
  const [invitedUsersPage, setInvitedUsersPage] = useState(1);
  const [invitedUsersLimit] = useState(10);
  const [invitedUsersTotalCount, setInvitedUsersTotalCount] = useState(0);
  const [invitedUsersTotalPages, setInvitedUsersTotalPages] = useState(0);
  const [inviteCodes, setInviteCodes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // 调试状态变化
  console.log('DashboardPage render - inviteCodes:', inviteCodes);
  
  // Token查询相关状态
  const [apiKey, setApiKey] = useState('');
  const [statsData, setStatsData] = useState<any>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState('');

  // 开卡管理相关状态
  const [selectedCardSystemId, setSelectedCardSystemId] = useState('');
  const [selectedCardSystem, setSelectedCardSystem] = useState<any>(null); // 存储完整的系统信息
  const [selectedAccountType, setSelectedAccountType] = useState<AccountPermissions>('claude'); // 新增账号类型状态
  const [systemList, setSystemList] = useState<Array<{ id: string; name: string; interface_url: string; claude_api_url: string; openai_api_url: string; description?: string }>>([]);

  // 处理系统选择变化
  const handleCardSystemChange = (systemId: string, systemInfo?: any) => {
    setSelectedCardSystemId(systemId);
    setSelectedCardSystem(systemInfo);
    console.log('📡 DashboardPage - 开卡系统已更改:', { systemId, systemInfo });
  };

  // 调试系统选择状态变化
  useEffect(() => {
    console.log('🔄 DashboardPage - selectedCardSystemId 状态变化:', selectedCardSystemId);
  }, [selectedCardSystemId]);
  const [cardLoading, setCardLoading] = useState(false);
  const [cardError, setCardError] = useState('');
  const [cardSuccess, setCardSuccess] = useState('');
  const [showWeekCardForm, setShowWeekCardForm] = useState(false);
  const [showMonthCardForm, setShowMonthCardForm] = useState(false);
  const [weekCardLoading, setWeekCardLoading] = useState(false);
  const [monthCardLoading, setMonthCardLoading] = useState(false);

  // 备注管理相关状态
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [noteContent, setNoteContent] = useState('');
  const [noteLoading, setNoteLoading] = useState(false);
  const [noteError, setNoteError] = useState('');
  const [noteSuccess, setNoteSuccess] = useState('');

  // API Key列表相关状态
  const [apiKeys, setApiKeys] = useState<ApiKeyDetail[]>([]);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [apiKeyError, setApiKeyError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalApiKeys, setTotalApiKeys] = useState(0);
  const [showApiKeys, setShowApiKeys] = useState(false);
  const [deletingKeyId, setDeletingKeyId] = useState<string | null>(null);

  // 修改密码相关状态
  const [showPasswordChangeForm, setShowPasswordChangeForm] = useState(false);
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  const [passwordChangeError, setPasswordChangeError] = useState('');
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState('');

  // API Key管理相关状态
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [boundApiKeys, setBoundApiKeys] = useState<any[]>([]);
  const [apiKeyModalLoading, setApiKeyModalLoading] = useState(false);
  const [apiKeyModalError, setApiKeyModalError] = useState('');
  const [apiKeyModalSuccess, setApiKeyModalSuccess] = useState('');

  useEffect(() => {
    loadDashboardData();
    loadBoundApiKeys(); // 加载已绑定的API Key用于快速选择
  }, []);

  useEffect(() => {
    loadInvitedUsers();
  }, [invitedUsersPage]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      // 并行加载数据（不包括邀请用户，邀请用户用单独的函数处理）
      const [inviteCodesResult, systemListResult] = await Promise.allSettled([
        inviteApi.list(),
        systemApi.getSystemsForSelection()
      ]);

      if (inviteCodesResult.status === 'fulfilled') {
        console.log('Loading invite codes:', inviteCodesResult.value);
        setInviteCodes(inviteCodesResult.value.codes || []);
        console.log('Set invite codes state:', inviteCodesResult.value.codes || []);
      } else {
        console.error('Failed to load invite codes:', inviteCodesResult.reason);
      }

      if (systemListResult.status === 'fulfilled') {
        console.log('Loading system list:', systemListResult.value);
        setSystemList(systemListResult.value);
      } else {
        console.error('Failed to load system list:', systemListResult.reason);
      }
    } catch (err) {
      console.error('Load dashboard data error:', err);
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('加载数据失败，请重试');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadInvitedUsers = async () => {
    try {
      const result = await authApi.getInvitedUsersPaginated(invitedUsersPage, invitedUsersLimit);
      setInvitedUsers(result.invited_users || []);
      setInvitedUsersTotalCount(result.total_count || 0);
      setInvitedUsersTotalPages(result.total_pages || 0);
    } catch (err) {
      console.error('Failed to load invited users:', err);
      // 不需要设置全局错误，这只是一个模块的错误
    }
  };

  const generateInviteCode = async () => {
    try {
      setError('');
      console.log('Generating invite code...');
      
      const result = await inviteApi.generate({ max_uses: 1, expires_in_hours: 168 }); // 7天有效期
      console.log('Invite code generated:', result);
      
      // 强制刷新邀请码列表
      try {
        const inviteCodesResult = await inviteApi.list();
        console.log('Refreshed invite codes:', inviteCodesResult);
        console.log('Setting new invite codes:', inviteCodesResult.codes || []);
        setInviteCodes(inviteCodesResult.codes || []);
        console.log('State update completed');
      } catch (refreshErr) {
        console.error('Failed to refresh invite codes:', refreshErr);
        // 如果刷新失败，仍然尝试重新加载所有数据
        await loadDashboardData();
      }
    } catch (err) {
      console.error('Generate invite code error:', err);
      setError(err instanceof Error ? err.message : '生成邀请码失败');
    }
  };

  // 删除邀请码
  const deleteInviteCode = async (codeId: string, codeValue: string) => {
    if (!window.confirm(`确定要删除邀请码 "${codeValue}" 吗？\n\n注意：只能删除未使用的邀请码，已使用的邀请码只能停用。`)) {
      return;
    }

    try {
      setError('');
      console.log('Deleting invite code:', codeId);
      
      await inviteApi.delete(codeId);
      console.log('Invite code deleted successfully');
      
      // 刷新邀请码列表
      const inviteCodesResult = await inviteApi.list();
      setInviteCodes(inviteCodesResult.codes || []);
      console.log('Invite codes refreshed after deletion');
    } catch (err) {
      console.error('Delete invite code error:', err);
      setError(err instanceof Error ? err.message : '删除邀请码失败');
    }
  };

  // 停用邀请码
  const deactivateInviteCode = async (codeId: string, codeValue: string) => {
    if (!window.confirm(`确定要停用邀请码 "${codeValue}" 吗？\n\n停用后该邀请码将无法使用，但记录会保留。`)) {
      return;
    }

    try {
      setError('');
      console.log('Deactivating invite code:', codeId);
      
      await inviteApi.deactivate(codeId);
      console.log('Invite code deactivated successfully');
      
      // 刷新邀请码列表
      const inviteCodesResult = await inviteApi.list();
      setInviteCodes(inviteCodesResult.codes || []);
      console.log('Invite codes refreshed after deactivation');
    } catch (err) {
      console.error('Deactivate invite code error:', err);
      setError(err instanceof Error ? err.message : '停用邀请码失败');
    }
  };

  // 查询积分统计
  const queryStats = async () => {
    if (!apiKey.trim()) {
      setStatsError('请输入API Key');
      return;
    }

    if (!selectedCardSystemId) {
      setStatsError('请先选择开卡系统');
      return;
    }

    // 根据选中的系统ID找到对应的系统配置
    const selectedSystem = systemList.find(system => system.id === selectedCardSystemId);
    if (!selectedSystem) {
      setStatsError('选中的系统不存在');
      return;
    }

    try {
      setStatsLoading(true);
      setStatsError('');
      setStatsData(null);

      console.log('Starting stats query with:', { 
        apiKey: apiKey.trim(), 
        systemId: selectedCardSystemId,
        baseURL: selectedSystem.interface_url
      });

      const authToken = localStorage.getItem('auth_token');
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      };

      // 第一步：通过后端代理API获取用户ID
      console.log('Step 1: Getting key ID through backend proxy API');
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';
      const getKeyIdUrl = `${API_BASE_URL}/api/v1/stats/get-key-id`;
      
      const keyResponse = await fetch(getKeyIdUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({ 
          apiKey: apiKey.trim(),
          systemId: selectedCardSystemId
        }),
      });

      if (!keyResponse.ok) {
        const errorText = await keyResponse.text();
        throw new Error(`请求失败: ${keyResponse.status} - ${errorText}`);
      }

      const keyResult = await keyResponse.json();
      console.log('Key result:', keyResult);

      if (!keyResult.success || !keyResult.data?.name) {
        throw new Error('无效的API Key');
      }

      // 第三方接口已变更：get-key-id 一步返回密钥的额度与使用信息，无需再调用 user-stats
      console.log('Setting stats data:', keyResult.data);
      setStatsData(keyResult.data);
    } catch (err) {
      console.error('Stats query error:', err);
      setStatsError(err instanceof Error ? err.message : '查询失败');
    } finally {
      setStatsLoading(false);
    }
  };

  // 创建日卡
  const createDayCard = async () => {
    if (!selectedCardSystemId) {
      setCardError('请选择开卡系统');
      return;
    }

    if (!selectedAccountType) {
      setCardError('请选择账号类型');
      return;
    }

    const accountTypeName = ACCOUNT_PERMISSIONS_CONFIG[selectedAccountType].name;
    if (!window.confirm(`确定要创建日卡吗？\n\n系统将使用选中的开卡系统创建一个新的日卡。\n账号类型: ${accountTypeName}`)) {
      return;
    }

    try {
      setCardLoading(true);
      setCardError('');
      setCardSuccess('');
      
      console.log('开始创建日卡...');
      console.log('Selected system ID:', selectedCardSystemId);
      console.log('Selected account type:', selectedAccountType);
      const result = await cardApi.createDayCard(selectedCardSystemId, selectedAccountType);
      console.log('日卡创建结果:', result);
      
      if (result.success && result.data) {
        const systemInfo = result.data.systemName && result.data.systemId
          ? `\n系统: ${result.data.systemName}（${result.data.systemId}）`
          : result.data.systemName
            ? `\n系统: ${result.data.systemName}`
            : '';
        // 根据账号类型选择相应的API URL
        const apiEndpoint = selectedCardSystem?.claude_api_url || `http://claudeai.baimaoai.top:30000/${selectedCardSystemId}/api/`;
        setCardSuccess(`日卡创建成功！\nAPI Key: ${result.data.apiKey}\n名称: ${result.data.name}\n有效期: 1天\nAPI地址: ${apiEndpoint}${systemInfo}`);
      } else {
        setCardError('创建日卡失败：响应数据异常');
      }
    } catch (err) {
      console.error('创建日卡错误:', err);
      setCardError(err instanceof Error ? err.message : '创建日卡失败');
    } finally {
      setCardLoading(false);
    }
  };

  // 创建周卡
  const createWeekCard = async (data: CreateCardRequest) => {
    if (!selectedCardSystemId) {
      setCardError('请选择开卡系统');
      return;
    }

    try {
      setWeekCardLoading(true);
      setCardError('');
      setCardSuccess('');

      console.log('开始创建周卡...');
      console.log('Week card data:', data);
      console.log('Selected system ID:', selectedCardSystemId);
      console.log('Selected account type:', selectedAccountType);
      console.log('Selected system type:', typeof selectedCardSystemId);
      // 使用日卡页面的账号类型选择器值
      const result = await cardApi.createWeekCard({
        ...data,
        permissions: selectedAccountType,
        systemId: selectedCardSystemId
      });
      console.log('周卡创建结果:', result);
      
      if (result.success && result.data) {
        const systemInfo = result.data.systemName && result.data.systemId
          ? `\n系统: ${result.data.systemName}（${result.data.systemId}）`
          : result.data.systemName
            ? `\n系统: ${result.data.systemName}`
            : '';
        // 根据账号类型选择相应的API URL
        const apiEndpoint = selectedCardSystem?.claude_api_url || `http://claudeai.baimaoai.top:30000/${selectedCardSystemId}/api/`;
        setCardSuccess(`周卡创建成功！\nAPI Key: ${result.data.apiKey}\n名称: ${result.data.name}\n有效期: 7天\nAPI地址: ${apiEndpoint}${systemInfo}`);
        setShowWeekCardForm(false);
      } else {
        setCardError('创建周卡失败：响应数据异常');
      }
    } catch (err) {
      console.error('创建周卡错误:', err);
      setCardError(err instanceof Error ? err.message : '创建周卡失败');
    } finally {
      setWeekCardLoading(false);
    }
  };

  // 创建月卡
  const createMonthCard = async (data: CreateCardRequest) => {
    if (!selectedCardSystemId) {
      setCardError('请选择开卡系统');
      return;
    }

    try {
      setMonthCardLoading(true);
      setCardError('');
      setCardSuccess('');

      console.log('开始创建月卡...');
      console.log('Month card data:', data);
      console.log('Selected system ID:', selectedCardSystemId);
      console.log('Selected account type:', selectedAccountType);
      console.log('Selected system type:', typeof selectedCardSystemId);
      // 使用日卡页面的账号类型选择器值
      const result = await cardApi.createMonthCard({
        ...data,
        permissions: selectedAccountType,
        systemId: selectedCardSystemId
      });
      console.log('月卡创建结果:', result);
      
      if (result.success && result.data) {
        const systemInfo = result.data.systemName && result.data.systemId
          ? `\n系统: ${result.data.systemName}（${result.data.systemId}）`
          : result.data.systemName
            ? `\n系统: ${result.data.systemName}`
            : '';
        // 根据账号类型选择相应的API URL
        const apiEndpoint = selectedCardSystem?.claude_api_url || `http://claudeai.baimaoai.top:30000/${selectedCardSystemId}/api/`;
        setCardSuccess(`月卡创建成功！\nAPI Key: ${result.data.apiKey}\n名称: ${result.data.name}\n有效期: 30天\nAPI地址: ${apiEndpoint}${systemInfo}`);
        setShowMonthCardForm(false);
      } else {
        setCardError('创建月卡失败：响应数据异常');
      }
    } catch (err) {
      console.error('创建月卡错误:', err);
      setCardError(err instanceof Error ? err.message : '创建月卡失败');
    } finally {
      setMonthCardLoading(false);
    }
  };

  // 打开备注弹窗
  const openNoteModal = (user: User) => {
    setEditingUser(user);
    setNoteContent(user.note?.note_content || '');
    setNoteError('');
    setNoteModalOpen(true);
    // 延迟聚焦以确保模态框已渲染
    setTimeout(() => {
      const textarea = document.querySelector('#note-textarea') as HTMLTextAreaElement;
      if (textarea) {
        textarea.focus();
        // 如果是编辑现有备注，将光标移到末尾
        if (user.note?.note_content) {
          textarea.setSelectionRange(textarea.value.length, textarea.value.length);
        }
      }
    }, 100);
  };

  // 关闭备注弹窗
  const closeNoteModal = () => {
    setNoteModalOpen(false);
    setEditingUser(null);
    setNoteContent('');
    setNoteError('');
    setNoteSuccess('');
  };

  // 保存备注
  const saveNote = async () => {
    if (!editingUser || noteLoading) return;
    
    const trimmedContent = noteContent.trim();
    if (!trimmedContent) {
      setNoteError('备注内容不能为空');
      return;
    }

    try {
      setNoteLoading(true);
      setNoteError('');

      if (editingUser.note) {
        // 更新现有备注
        await authApi.updateUserNote(editingUser.user_id, trimmedContent);
      } else {
        // 添加新备注
        await authApi.addUserNote(editingUser.user_id, trimmedContent);
      }

      // 刷新邀请用户列表
      await loadDashboardData();
      
      // 显示成功消息并延迟关闭弹窗
      setNoteSuccess(editingUser.note ? '备注已更新' : '备注已添加');
      setTimeout(() => {
        closeNoteModal();
      }, 1000);
    } catch (err) {
      console.error('保存备注错误:', err);
      setNoteError(err instanceof Error ? err.message : '保存备注失败');
    } finally {
      setNoteLoading(false);
    }
  };

  // 删除备注
  const deleteNote = async (userId: string) => {
    if (!window.confirm('确定要删除该用户的备注吗？')) {
      return;
    }

    try {
      setError('');
      await authApi.deleteUserNote(userId);
      // 刷新邀请用户列表
      await loadDashboardData();
    } catch (err) {
      console.error('删除备注错误:', err);
      setError(err instanceof Error ? err.message : '删除备注失败');
    }
  };

  // 查询API Key列表
  const loadApiKeys = async (page: number = 1) => {
    if (!selectedCardSystemId) {
      setApiKeyError('请先选择系统');
      return;
    }

    try {
      setApiKeyLoading(true);
      setApiKeyError('');
      
      console.log('Loading API Keys for user:', user?.email, 'system:', selectedCardSystemId);
      
      const result = await cardApi.listApiKeys({
        page,
        size: 10,
        mask: true,
        systemId: selectedCardSystemId
      });
      
      console.log('API Keys loaded:', result);
      
      setApiKeys(result.items);
      setTotalApiKeys(result.total);
      setCurrentPage(page);
    } catch (err) {
      console.error('Load API Keys error:', err);
      setApiKeyError(err instanceof Error ? err.message : '查询API Key列表失败');
    } finally {
      setApiKeyLoading(false);
    }
  };

  // 切换API Key列表显示状态
  const toggleApiKeysView = async () => {
    if (!selectedCardSystemId) {
      setApiKeyError('请先选择系统');
      return;
    }

    if (!showApiKeys) {
      await loadApiKeys(1);
    }
    setShowApiKeys(!showApiKeys);
  };

  // 删除API Key
  const deleteApiKey = async (keyId: string, keyName: string) => {
    if (!selectedCardSystemId) {
      setApiKeyError('请先选择系统');
      return;
    }

    if (!window.confirm(`确定要删除API Key "${keyName}" 吗？\n\n删除后该API Key将永久失效，无法恢复。`)) {
      return;
    }

    try {
      setDeletingKeyId(keyId);
      setApiKeyError('');
      
      console.log('开始删除API Key:', keyId, 'system:', selectedCardSystemId);
      await cardApi.deleteApiKey(keyId, selectedCardSystemId);
      console.log('API Key删除成功');
      
      // 刷新API Key列表
      await loadApiKeys(currentPage);
      
      // 显示成功消息
      alert('API Key删除成功！');
    } catch (err) {
      console.error('删除API Key错误:', err);
      setApiKeyError(err instanceof Error ? err.message : '删除API Key失败');
    } finally {
      setDeletingKeyId(null);
    }
  };

  // 解析JSON格式的tags
  const parseTags = (tagsStr: string): string[] => {
    try {
      const parsed = JSON.parse(tagsStr);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return tagsStr ? tagsStr.split(',').map(tag => tag.trim()) : [];
    }
  };

  // 格式化时间显示
  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return '未知';
    try {
      return new Date(dateStr).toLocaleString('zh-CN');
    } catch {
      return dateStr;
    }
  };

  const handleLogout = () => {
    logout();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('zh-CN');
  };

  // 修改密码
  const handlePasswordChange = async (formData: PasswordChangeData) => {
    try {
      setPasswordChangeLoading(true);
      setPasswordChangeError('');
      setPasswordChangeSuccess('');

      const changePasswordData: ChangePasswordRequest = {
        old_password: formData.oldPassword,
        new_password: formData.newPassword
      };

      await authApi.changePassword(changePasswordData);
      
      setPasswordChangeSuccess('密码修改成功！系统将在3秒后自动退出登录...');
      
      // 3秒后自动退出登录
      setTimeout(() => {
        logout();
      }, 3000);
      
    } catch (err) {
      console.error('Password change error:', err);
      if (err instanceof ApiError) {
        setPasswordChangeError(err.message);
      } else {
        setPasswordChangeError('修改密码失败，请重试');
      }
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  // 取消修改密码
  const handleCancelPasswordChange = () => {
    setShowPasswordChangeForm(false);
    setPasswordChangeError('');
    setPasswordChangeSuccess('');
  };

  // API Key管理相关函数
  const loadBoundApiKeys = async () => {
    try {
      setApiKeyModalLoading(true);
      setApiKeyModalError('');
      const keys = await authApi.getBoundApiKeys();
      setBoundApiKeys(keys || []);
    } catch (err) {
      console.error('Load bound API keys error:', err);
      setApiKeyModalError(err instanceof Error ? err.message : '加载API Key失败');
    } finally {
      setApiKeyModalLoading(false);
    }
  };

  const handleOpenApiKeyModal = async () => {
    setShowApiKeyModal(true);
    await loadBoundApiKeys();
  };

  const handleCloseApiKeyModal = () => {
    setShowApiKeyModal(false);
    setApiKeyModalError('');
    setApiKeyModalSuccess('');
  };

  const handleBindApiKey = async (data: any) => {
    try {
      setApiKeyModalLoading(true);
      setApiKeyModalError('');
      await authApi.bindApiKey(data);
      setApiKeyModalSuccess(`API Key "${data.key_name}" 绑定成功！`);
      await loadBoundApiKeys();
      // 刷新用户信息
      await refreshUser();
      // 3秒后清除成功消息
      setTimeout(() => {
        setApiKeyModalSuccess('');
      }, 3000);
    } catch (err) {
      console.error('Bind API Key error:', err);
      setApiKeyModalError(err instanceof Error ? err.message : '绑定API Key失败');
    } finally {
      setApiKeyModalLoading(false);
    }
  };

  const handleUnbindApiKey = async (apiKey: string, keyName: string) => {
    if (!window.confirm(`确定要解绑API Key "${keyName}" 吗？`)) {
      return;
    }

    try {
      setApiKeyModalLoading(true);
      setApiKeyModalError('');
      await authApi.unbindApiKey({ api_key: apiKey });
      setApiKeyModalSuccess(`API Key "${keyName}" 已解绑！`);
      await loadBoundApiKeys();
      // 刷新用户信息
      await refreshUser();
      // 3秒后清除成功消息
      setTimeout(() => {
        setApiKeyModalSuccess('');
      }, 3000);
    } catch (err) {
      console.error('Unbind API Key error:', err);
      setApiKeyModalError(err instanceof Error ? err.message : '解绑API Key失败');
    } finally {
      setApiKeyModalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Container>
          <div className="py-20 text-center">
            <div className="text-2xl text-gray-600">加载中...</div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <Container>
          <div className="flex items-center justify-between py-4">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-primary-600 to-accent-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">白</span>
              </div>
              <span className="text-xl font-bold text-gray-900">白茆AI</span>
            </Link>

            <div className="flex items-center space-x-4">
              <span className="text-gray-700">欢迎，{user?.email}</span>
              <Button onClick={handleLogout} variant="outline" size="sm">
                退出登录
              </Button>
            </div>
          </div>
        </Container>
      </div>

      <Container>
        <div className="py-8">
          {/* 页面标题 */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">管理面板</h1>
            <p className="text-gray-600">管理您的账户、FAQ内容、联系信息和AI应用</p>
          </div>

          {/* 标签导航 */}
          <div className="mb-8">
            <nav className="flex space-x-8">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'overview'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                账户概览
              </button>
              {user?.role === 'admin' && (
                <>
                  <button
                    onClick={() => setActiveTab('faq')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'faq'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    FAQ管理
                  </button>
                  <button
                    onClick={() => setActiveTab('contact')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'contact'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    联系管理
                  </button>
                  <button
                    onClick={() => setActiveTab('aiapp')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'aiapp'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    AI应用管理
                  </button>
                </>
              )}
            </nav>
          </div>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex">
                <div className="text-red-400 mr-3">⚠️</div>
                <div>
                  <h3 className="text-sm font-medium text-red-800">错误</h3>
                  <p className="mt-1 text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* 标签内容区域 */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* 用户信息卡片 */}
            <Card className="lg:col-span-1">
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">账户信息</h2>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">用户ID</label>
                    <p className="text-gray-900 font-mono text-sm">{user?.user_id}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">邮箱地址</label>
                    <p className="text-gray-900">{user?.email}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">账户状态</label>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user?.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {user?.is_active ? '已激活' : '未激活'}
                    </span>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">用户角色</label>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user?.role === 'admin'
                        ? 'bg-purple-100 text-purple-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {user?.role === 'admin' ? '管理员' : '普通用户'}
                    </span>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">注册时间</label>
                    <p className="text-gray-900 text-sm">
                      {user?.created_at ? formatDate(user.created_at) : '未知'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">最后登录</label>
                    <p className="text-gray-900 text-sm">
                      {user?.last_login_at ? formatDate(user.last_login_at) : '从未登录'}
                    </p>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-gray-200 space-y-2">
                  <Button onClick={refreshUser} variant="outline" size="sm" className="w-full">
                    刷新信息
                  </Button>
                  <Button 
                    onClick={() => setShowPasswordChangeForm(true)} 
                    variant="outline" 
                    size="sm" 
                    className="w-full text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-200 hover:border-blue-300"
                  >
                    🔒 修改密码
                  </Button>
                  <Button 
                    onClick={handleOpenApiKeyModal} 
                    variant="outline" 
                    size="sm" 
                    className="w-full text-green-600 hover:text-green-700 hover:bg-green-50 border-green-200 hover:border-green-300"
                  >
                    🔑 管理我的Key
                  </Button>
                </div>
              </div>
            </Card>

            {/* 邀请码管理 */}
            <Card className="lg:col-span-2">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">邀请码管理</h2>
                  <Button onClick={generateInviteCode} size="sm">
                    生成邀请码
                  </Button>
                </div>

                {inviteCodes.length > 0 ? (
                  <div className="space-y-3">
                    {inviteCodes.map((code) => (
                      <div key={code.id || code.code} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="font-mono text-lg font-semibold text-primary-600">
                              {code.code}
                            </p>
                            <p className="text-sm text-gray-600">
                              使用次数: {code.use_count || 0}/{code.max_uses || 1} | 
                              创建时间: {formatDate(code.created_at)}
                            </p>
                          </div>
                          <div className="flex items-center space-x-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              code.is_active 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {code.is_active ? '有效' : '已失效'}
                            </span>
                            <div className="flex space-x-2">
                              {code.is_active && (
                                <Button
                                  onClick={() => deactivateInviteCode(code.id, code.code)}
                                  variant="outline"
                                  size="sm"
                                  className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                >
                                  停用
                                </Button>
                              )}
                              <Button
                                onClick={() => deleteInviteCode(code.id, code.code)}
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                删除
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-gray-400 text-4xl mb-4">🎫</div>
                    <p className="text-gray-500 mb-4">暂无邀请码</p>
                    <Button onClick={generateInviteCode} size="sm">
                      生成第一个邀请码
                    </Button>
                  </div>
                )}
              </div>
            </Card>

            {/* 邀请的用户 */}
            <Card className="lg:col-span-3">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">
                    我邀请的用户 (共{invitedUsersTotalCount}个，当前显示第{invitedUsersPage}页)
                  </h2>
                  {invitedUsersTotalPages > 1 && (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setInvitedUsersPage(prev => Math.max(1, prev - 1))}
                        disabled={invitedUsersPage <= 1}
                        className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        上一页
                      </button>
                      <span className="text-sm text-gray-600">
                        {invitedUsersPage} / {invitedUsersTotalPages}
                      </span>
                      <button
                        onClick={() => setInvitedUsersPage(prev => Math.min(invitedUsersTotalPages, prev + 1))}
                        disabled={invitedUsersPage >= invitedUsersTotalPages}
                        className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        下一页
                      </button>
                    </div>
                  )}
                </div>

                {invitedUsers.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            邮箱
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            状态
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            注册时间
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            最后登录
                          </th>
                          {user?.role === 'admin' && (
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              备注
                            </th>
                          )}
                          {user?.role === 'admin' && (
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              操作
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {invitedUsers.map((invitedUser) => (
                          <tr key={invitedUser.user_id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {invitedUser.email}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                invitedUser.is_active 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {invitedUser.is_active ? '已激活' : '未激活'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(invitedUser.registered_at || invitedUser.created_at)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {invitedUser.last_login_at ? formatDate(invitedUser.last_login_at) : '从未登录'}
                            </td>
                            {user?.role === 'admin' && (
                              <td className="px-6 py-4 text-sm text-gray-500 max-w-0">
                                {invitedUser.note ? (
                                  <div className="max-w-xs">
                                    <p 
                                      className="text-gray-900 truncate cursor-help hover:text-primary-600" 
                                      title={`备注：${invitedUser.note.note_content}\n更新时间：${formatDate(invitedUser.note.updated_at)}`}
                                    >
                                      {invitedUser.note.note_content}
                                    </p>
                                    <p className="text-xs text-gray-400 mt-1">
                                      {formatDate(invitedUser.note.updated_at)}
                                    </p>
                                  </div>
                                ) : (
                                  <span className="text-gray-400 italic">暂无备注</span>
                                )}
                              </td>
                            )}
                            {user?.role === 'admin' && (
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <div className="flex justify-end space-x-2">
                                  <button
                                    onClick={() => openNoteModal(invitedUser)}
                                    className="text-indigo-600 hover:text-indigo-900 text-sm"
                                  >
                                    {invitedUser.note ? '编辑备注' : '添加备注'}
                                  </button>
                                  {invitedUser.note && (
                                    <button
                                      onClick={() => deleteNote(invitedUser.user_id)}
                                      className="text-red-600 hover:text-red-900 text-sm ml-3"
                                    >
                                      删除备注
                                    </button>
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-gray-400 text-4xl mb-4">👥</div>
                    <p className="text-gray-500 mb-4">还没有邀请过用户</p>
                    <p className="text-sm text-gray-400">
                      生成邀请码并分享给朋友，邀请他们加入白茆AI
                    </p>
                  </div>
                )}
              </div>
            </Card>

            {/* 开卡管理 - 仅管理员可见 */}
            {user?.role === 'admin' && (
              <Card className="lg:col-span-3">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-gray-900">开卡管理</h2>
                </div>
                
                {/* 系统选择 */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    选择开卡系统 *
                  </label>
                  <SystemSelector
                    value={selectedCardSystemId}
                    onChange={handleCardSystemChange}
                    className="max-w-md"
                    placeholder="选择用于开卡的系统"
                    required={true}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    选择的系统将用于创建所有新的API Key
                  </p>
                </div>

                {/* 账号类型选择 - 统一使用共享组件 */}
                <div className="mb-6">
                  <AccountTypeSelector
                    selectedType={selectedAccountType}
                    onTypeChange={setSelectedAccountType}
                  />
                  <p className="mt-2 text-xs text-gray-500">
                    选择的账号类型将应用到所有新创建的卡片（日卡、周卡、月卡）
                  </p>
                </div>

                {/* 快速开卡选项 */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  {/* 日卡 */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                    <div className="text-center">
                      <h3 className="text-md font-semibold text-gray-900 mb-2">日卡 (1天)</h3>
                      <p className="text-gray-600 text-xs mb-4">
                        快速创建，固定配置：
                        <br />• 日消费限制: $12
                        <br />• 并发限制: 3个终端
                        <br />• 模型限制: 禁用高级模型
                      </p>
                      <Button
                        onClick={createDayCard}
                        disabled={cardLoading || !selectedCardSystemId || !selectedAccountType}
                        size="sm"
                        className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50"
                      >
                        {cardLoading ? '创建中...' : '🎫 快速开日卡'}
                      </Button>
                      {(!selectedCardSystemId || !selectedAccountType) && (
                        <p className="text-xs text-red-500 mt-2">
                          {!selectedCardSystemId && '请先选择系统'}
                          {!selectedCardSystemId && !selectedAccountType && '，'}
                          {!selectedAccountType && '请选择账号类型'}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* 周卡 */}
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-200">
                    <div className="text-center">
                      <h3 className="text-md font-semibold text-gray-900 mb-2">周卡 (7天)</h3>
                      <p className="text-gray-600 text-xs mb-4">
                        自定义配置：
                        <br />• 产品型号可选
                        <br />• 自定义并发数和限额
                        <br />• 可设置Token限制
                      </p>
                      <Button 
                        onClick={() => {
                          setShowWeekCardForm(!showWeekCardForm);
                          if (!showWeekCardForm) {
                            setShowMonthCardForm(false); // 关闭月卡表单
                          }
                        }}
                        disabled={weekCardLoading}
                        size="sm"
                        variant={showWeekCardForm ? "outline" : "primary"}
                        className={`w-full ${!showWeekCardForm ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700' : ''}`}
                      >
                        {showWeekCardForm ? '取消' : '⚙️ 配置周卡'}
                      </Button>
                    </div>
                  </div>

                  {/* 月卡 */}
                  <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-lg p-4 border border-emerald-200">
                    <div className="text-center">
                      <h3 className="text-md font-semibold text-gray-900 mb-2">月卡 (30天)</h3>
                      <p className="text-gray-600 text-xs mb-4">
                        自定义配置：
                        <br />• 产品型号可选
                        <br />• 自定义并发数和限额
                        <br />• 可设置Token限制
                      </p>
                      <Button 
                        onClick={() => {
                          setShowMonthCardForm(!showMonthCardForm);
                          if (!showMonthCardForm) {
                            setShowWeekCardForm(false); // 关闭周卡表单
                          }
                        }}
                        disabled={monthCardLoading}
                        size="sm"
                        variant={showMonthCardForm ? "outline" : "primary"}
                        className={`w-full ${!showMonthCardForm ? 'bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700' : ''}`}
                      >
                        {showMonthCardForm ? '取消' : '⚙️ 配置月卡'}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* 周卡创建表单 */}
                {showWeekCardForm && (
                  <div className="mb-6">
                    <CardCreationForm
                      cardType="week"
                      loading={weekCardLoading}
                      onSubmit={createWeekCard}
                      onCancel={() => setShowWeekCardForm(false)}
                      accountType={selectedAccountType}
                    />
                  </div>
                )}

                {/* 月卡创建表单 */}
                {showMonthCardForm && (
                  <div className="mb-6">
                    <CardCreationForm
                      cardType="month"
                      loading={monthCardLoading}
                      onSubmit={createMonthCard}
                      onCancel={() => setShowMonthCardForm(false)}
                      accountType={selectedAccountType}
                    />
                  </div>
                )}

                {/* 错误信息 */}
                {cardError && (
                  <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <div className="text-red-400 mr-3 mt-1">⚠️</div>
                      <div>
                        <h3 className="text-sm font-medium text-red-800">创建失败</h3>
                        <p className="mt-1 text-sm text-red-700 whitespace-pre-wrap">{cardError}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* 成功信息 */}
                {cardSuccess && (
                  <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <div className="text-green-400 mr-3 mt-1">✅</div>
                      <div>
                        <h3 className="text-sm font-medium text-green-800">创建成功</h3>
                        <p className="mt-1 text-sm text-green-700 whitespace-pre-wrap font-mono">{cardSuccess}</p>
                        <div className="mt-3">
                          <button
                            onClick={() => {
                              const apiKey = cardSuccess.match(/API Key: (cr_[a-f0-9]+)/)?.[1];
                              if (apiKey) {
                                navigator.clipboard.writeText(apiKey);
                                alert('API Key已复制到剪贴板！');
                              }
                            }}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-green-700 bg-green-100 hover:bg-green-200"
                          >
                            📋 复制 API Key
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Token查询区域 */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Tokens查询</h3>
                  </div>

                  {/* 快速选择已绑定的Key */}
                  {boundApiKeys.length > 0 && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        快速选择已绑定的Key
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {boundApiKeys.map((key) => (
                          <button
                            key={key.api_key}
                            onClick={() => {
                              setApiKey(key.api_key);
                              setSelectedCardSystemId(key.system_id);
                            }}
                            className="px-3 py-1 text-sm bg-blue-50 text-blue-700 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
                            title={`系统: ${key.system_name}\n备注: ${key.notes || '无'}`}
                          >
                            {key.key_name} ({key.system_name})
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 查询输入框 */}
                  <div className="flex gap-4 mb-6">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !statsLoading && apiKey.trim() && selectedCardSystemId) {
                            queryStats();
                          }
                        }}
                        placeholder="请输入API Key (例如: cr_82018a1f1c3c8648be2931b3e78d808da3c0493612d1f024879051a7c7434865)"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        disabled={statsLoading}
                      />
                    </div>
                    <Button
                      onClick={queryStats}
                      disabled={statsLoading || !apiKey.trim() || !selectedCardSystemId}
                      className="whitespace-nowrap"
                    >
                      {statsLoading ? '查询中...' : '查询积分'}
                    </Button>
                  </div>

                  {/* 错误信息 */}
                  {statsError && (
                    <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
                      <div className="flex items-center">
                        <div className="text-red-400 mr-2">⚠️</div>
                        <p className="text-sm text-red-700">{statsError}</p>
                      </div>
                    </div>
                  )}

                  {/* 查询结果 */}
                  {statsData && (
                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                      <h4 className="text-md font-semibold text-gray-900 mb-4">API Key使用统计</h4>

                      {/* API Key名称 */}
                      <div className="mb-6">
                        <div className="bg-white rounded-lg p-4 border border-gray-200">
                          <div className="text-sm font-medium text-gray-500 mb-1">API Key名称</div>
                          <div className="text-lg font-semibold text-gray-900">{statsData.name || 'N/A'}</div>
                        </div>
                      </div>

                      {/* 额度使用 */}
                      <div className="mb-2">
                        <h5 className="text-lg font-semibold text-gray-900 mb-3">💰 额度使用</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                          <div className="bg-white rounded-lg p-4 border border-gray-200">
                            <div className="text-sm font-medium text-gray-500 mb-1">总限额</div>
                            <div className="text-2xl font-bold text-gray-700">
                              {statsData.quota ? `$${Number(statsData.quota).toFixed(2)}` : '不限'}
                            </div>
                          </div>
                          <div className="bg-white rounded-lg p-4 border border-gray-200">
                            <div className="text-sm font-medium text-gray-500 mb-1">总使用量</div>
                            <div className="text-2xl font-bold text-red-600">
                              ${Number(statsData.quota_used || 0).toFixed(4)}
                            </div>
                          </div>
                          <div className="bg-white rounded-lg p-4 border border-gray-200">
                            <div className="text-sm font-medium text-gray-500 mb-1">每日限额</div>
                            <div className="text-2xl font-bold text-gray-700">
                              {statsData.rate_limit_1d ? `$${Number(statsData.rate_limit_1d).toFixed(2)}` : '不限'}
                            </div>
                          </div>
                          <div className="bg-white rounded-lg p-4 border border-gray-200">
                            <div className="text-sm font-medium text-gray-500 mb-1">今日使用量</div>
                            <div className="text-2xl font-bold text-orange-600">
                              ${Number(statsData.usage_1d || 0).toFixed(4)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 空状态 */}
                  {!statsData && !statsLoading && !statsError && (
                    <div className="text-center py-6 mb-6">
                      <div className="text-gray-400 text-3xl mb-2">📊</div>
                      <p className="text-gray-500 text-sm mb-2">输入API Key查询消耗统计</p>
                      <p className="text-xs text-gray-400">
                        支持cr_开头的API密钥查询，需要先选择对应的开卡系统
                      </p>
                    </div>
                  )}
                  
                  {/* 系统选择提示 */}
                  {!selectedCardSystemId && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                      <div className="flex items-center">
                        <div className="text-blue-400 mr-2">💡</div>
                        <p className="text-sm text-blue-700">请先在上方选择开卡系统，然后输入对应系统的API Key进行查询</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* API Key列表区域 */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">我的API Key</h3>
                    <Button 
                      onClick={toggleApiKeysView}
                      variant="outline"
                      size="sm"
                      disabled={apiKeyLoading}
                    >
                      {apiKeyLoading ? '查询中...' : showApiKeys ? '隐藏列表' : '查看列表'}
                    </Button>
                  </div>


                  {/* API Key列表错误信息 */}
                  {apiKeyError && (
                    <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
                      <div className="flex items-center">
                        <div className="text-red-400 mr-2">⚠️</div>
                        <p className="text-sm text-red-700">{apiKeyError}</p>
                      </div>
                    </div>
                  )}

                  {/* API Key列表 */}
                  {showApiKeys && (
                    <div className="space-y-4">
                      {apiKeys.length > 0 ? (
                        <>
                          {/* 列表头部统计 */}
                          <div className="bg-blue-50 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="text-sm font-medium text-blue-900">
                                  找到 {totalApiKeys} 个与您邮箱相关的API Key
                                </h4>
                                <p className="text-xs text-blue-700 mt-1">
                                  找到与您邮箱相关的API Key
                                </p>
                              </div>
                              <div className="text-blue-600">
                                📊
                              </div>
                            </div>
                          </div>

                          {/* API Key卡片列表 */}
                          <div className="space-y-3">
                            {apiKeys.map((apiKey) => (
                              <div key={apiKey.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                  {/* 基本信息 */}
                                  <div className="md:col-span-2">
                                    <div className="flex items-center justify-between mb-2">
                                      <h5 className="font-medium text-gray-900 truncate pr-2">
                                        {apiKey.name}
                                      </h5>
                                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                        apiKey.isActive === 'true' 
                                          ? 'bg-green-100 text-green-800' 
                                          : 'bg-red-100 text-red-800'
                                      }`}>
                                        {apiKey.isActive === 'true' ? '激活' : '未激活'}
                                      </span>
                                    </div>
                                    
                                    <div className="text-sm text-gray-600 space-y-1">
                                      <div className="flex items-center">
                                        <span className="font-mono bg-gray-100 px-2 py-1 rounded text-xs">
                                          {apiKey.apiKey}
                                        </span>
                                        <button 
                                          onClick={() => {
                                            navigator.clipboard.writeText(apiKey.apiKey);
                                            alert('API Key已复制到剪贴板！');
                                          }}
                                          className="ml-2 text-blue-600 hover:text-blue-800 text-xs"
                                        >
                                          📋 复制
                                        </button>
                                      </div>
                                      
                                      {apiKey.description && (
                                        <p className="text-gray-500">{apiKey.description}</p>
                                      )}
                                      
                                      <div className="flex flex-wrap gap-1 mt-2">
                                        {parseTags(apiKey.tags).map((tag, index) => (
                                          <span 
                                            key={index}
                                            className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800"
                                          >
                                            {tag}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  </div>

                                  {/* 限制和状态信息 */}
                                  <div className="space-y-2 text-sm">
                                    <div className="grid grid-cols-2 gap-2">
                                      <div>
                                        <span className="text-gray-500">日消费限制:</span>
                                        <p className="font-medium">${apiKey.dailyCostLimit}</p>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">并发限制:</span>
                                        <p className="font-medium">{apiKey.concurrencyLimit}</p>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">过期时间:</span>
                                        <p className="font-medium text-xs">
                                          {formatDateTime(apiKey.expiresAt)}
                                        </p>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">TTL:</span>
                                        <p className="font-medium">{apiKey.redis_ttl_string}</p>
                                      </div>
                                    </div>
                                    
                                    <div className="pt-2 border-t border-gray-100">
                                      <span className="text-gray-500 text-xs">创建于:</span>
                                      <p className="text-xs">{formatDateTime(apiKey.createdAt)}</p>
                                    </div>

                                    {/* 删除按钮 */}
                                    <div className="pt-3 border-t border-gray-200">
                                      <Button
                                        onClick={() => deleteApiKey(apiKey.id, apiKey.name)}
                                        disabled={deletingKeyId === apiKey.id}
                                        variant="outline"
                                        size="sm"
                                        className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 hover:border-red-300 disabled:opacity-50"
                                      >
                                        {deletingKeyId === apiKey.id ? (
                                          <>
                                            <span className="animate-spin mr-2">⏳</span>
                                            删除中...
                                          </>
                                        ) : (
                                          <>
                                            🗑️ 删除API Key
                                          </>
                                        )}
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* 分页控制（如果需要的话） */}
                          {totalApiKeys > 10 && (
                            <div className="flex justify-center mt-4">
                              <div className="flex space-x-2">
                                <Button
                                  onClick={() => loadApiKeys(currentPage - 1)}
                                  disabled={currentPage <= 1 || apiKeyLoading}
                                  variant="outline"
                                  size="sm"
                                >
                                  上一页
                                </Button>
                                <span className="px-3 py-1.5 text-sm text-gray-600">
                                  {currentPage} / {Math.ceil(totalApiKeys / 10)}
                                </span>
                                <Button
                                  onClick={() => loadApiKeys(currentPage + 1)}
                                  disabled={currentPage >= Math.ceil(totalApiKeys / 10) || apiKeyLoading}
                                  variant="outline"
                                  size="sm"
                                >
                                  下一页
                                </Button>
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-center py-8">
                          <div className="text-gray-400 text-4xl mb-4">🔍</div>
                          <p className="text-gray-500 mb-2">未找到与您邮箱相关的API Key</p>
                          <p className="text-sm text-gray-400">
                            创建新的日卡后，它会出现在这里
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* 未展开时的提示 */}
                  {!showApiKeys && (
                    <div className="text-center py-6">
                      <div className="text-gray-400 text-3xl mb-2">🔑</div>
                      <p className="text-gray-500 text-sm">
                        点击"查看列表"按钮查看您的API Key
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </Card>
            )}


            {/* Tokens查询 - 仅普通用户可见 */}
            {user?.role !== 'admin' && (
            <Card className="lg:col-span-3">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-gray-900">Tokens查询</h2>
                </div>
                
                {/* 系统选择 - 普通用户版本 */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    选择查询系统 *
                  </label>
                  <SystemSelector
                    value={selectedCardSystemId}
                    onChange={handleCardSystemChange}
                    className="max-w-md"
                    placeholder="选择用于查询的系统节点"
                    required={true}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    选择对应的系统节点来查询您的API Key积分信息
                  </p>
                </div>

                {/* 快速选择已绑定的Key */}
                {boundApiKeys.length > 0 && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      快速选择已绑定的Key
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {boundApiKeys.map((key) => (
                        <button
                          key={key.api_key}
                          onClick={() => {
                            setApiKey(key.api_key);
                            setSelectedCardSystemId(key.system_id);
                          }}
                          className="px-3 py-1 text-sm bg-blue-50 text-blue-700 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
                          title={`系统: ${key.system_name}\n备注: ${key.notes || '无'}`}
                        >
                          {key.key_name} ({key.system_name})
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* 查询输入框 */}
                <div className="flex gap-4 mb-6">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !statsLoading && apiKey.trim() && selectedCardSystemId) {
                          queryStats();
                        }
                      }}
                      placeholder="请输入API Key (例如: cr_82018a1f1c3c8648be2931b3e78d808da3c0493612d1f024879051a7c7434865)"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      disabled={statsLoading}
                    />
                  </div>
                  <Button 
                    onClick={queryStats}
                    disabled={statsLoading || !apiKey.trim() || !selectedCardSystemId}
                    className="whitespace-nowrap"
                  >
                    {statsLoading ? '查询中...' : '查询积分'}
                  </Button>
                </div>

                {/* 错误信息 */}
                {statsError && (
                  <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-center">
                      <div className="text-red-400 mr-2">⚠️</div>
                      <p className="text-sm text-red-700">{statsError}</p>
                    </div>
                  </div>
                )}

                {/* 查询结果 */}
                {statsData && (
                  <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <h4 className="text-md font-semibold text-gray-900 mb-4">API Key使用统计</h4>

                    {/* API Key名称 */}
                    <div className="mb-6">
                      <div className="bg-white rounded-lg p-4 border border-gray-200">
                        <div className="text-sm font-medium text-gray-500 mb-1">API Key名称</div>
                        <div className="text-lg font-semibold text-gray-900">{statsData.name || 'N/A'}</div>
                      </div>
                    </div>

                    {/* 额度使用 */}
                    <div className="mb-2">
                      <h5 className="text-lg font-semibold text-gray-900 mb-3">💰 额度使用</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-white rounded-lg p-4 border border-gray-200">
                          <div className="text-sm font-medium text-gray-500 mb-1">总限额</div>
                          <div className="text-2xl font-bold text-gray-700">
                            {statsData.quota ? `$${Number(statsData.quota).toFixed(2)}` : '不限'}
                          </div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-gray-200">
                          <div className="text-sm font-medium text-gray-500 mb-1">总使用量</div>
                          <div className="text-2xl font-bold text-red-600">
                            ${Number(statsData.quota_used || 0).toFixed(4)}
                          </div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-gray-200">
                          <div className="text-sm font-medium text-gray-500 mb-1">每日限额</div>
                          <div className="text-2xl font-bold text-gray-700">
                            {statsData.rate_limit_1d ? `$${Number(statsData.rate_limit_1d).toFixed(2)}` : '不限'}
                          </div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-gray-200">
                          <div className="text-sm font-medium text-gray-500 mb-1">今日使用量</div>
                          <div className="text-2xl font-bold text-orange-600">
                            ${Number(statsData.usage_1d || 0).toFixed(4)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* 空状态 */}
                {!statsData && !statsLoading && !statsError && (
                  <div className="text-center py-6 mb-6">
                    <div className="text-gray-400 text-3xl mb-2">📊</div>
                    <p className="text-gray-500 text-sm mb-2">输入API Key查询消耗统计</p>
                    <p className="text-xs text-gray-400">
                      支持cr_开头的API密钥查询，需要先选择对应的系统节点
                    </p>
                  </div>
                )}
                
                {/* 系统选择提示 */}
                {!selectedCardSystemId && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                    <div className="flex items-center">
                      <div className="text-blue-400 mr-2">💡</div>
                      <p className="text-sm text-blue-700">请先选择系统节点，然后输入对应系统的API Key进行查询</p>
                    </div>
                  </div>
                )}
              </div>
            </Card>
            )}

            </div>
          )}

          {/* FAQ管理标签 */}
          {activeTab === 'faq' && user?.role === 'admin' && (
            <FAQManagement />
          )}

          {/* 联系管理标签 */}
          {activeTab === 'contact' && user?.role === 'admin' && (
            <ContactManagement />
          )}

          {/* AI应用管理标签 */}
          {activeTab === 'aiapp' && user?.role === 'admin' && (
            <AIAppManagement />
          )}
        </div>
      </Container>

      {/* 修改密码弹窗 */}
      {showPasswordChangeForm && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleCancelPasswordChange();
            }
          }}
        >
          <div 
            className="bg-white rounded-lg w-full max-w-lg mx-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">修改登录密码</h2>
                <button
                  onClick={handleCancelPasswordChange}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                  disabled={passwordChangeLoading}
                >
                  ×
                </button>
              </div>

              {/* 错误信息 */}
              {passwordChangeError && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="text-red-400 mr-3">⚠️</div>
                    <div>
                      <h3 className="text-sm font-medium text-red-800">修改失败</h3>
                      <p className="mt-1 text-sm text-red-700">{passwordChangeError}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* 成功信息 */}
              {passwordChangeSuccess && (
                <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="text-green-400 mr-3">✅</div>
                    <div>
                      <h3 className="text-sm font-medium text-green-800">修改成功</h3>
                      <p className="mt-1 text-sm text-green-700">{passwordChangeSuccess}</p>
                    </div>
                  </div>
                </div>
              )}

              <PasswordChangeForm
                onSubmit={handlePasswordChange}
                onCancel={handleCancelPasswordChange}
                loading={passwordChangeLoading}
              />
            </div>
          </div>
        </div>
      )}

      {/* 备注管理弹窗 */}
      {noteModalOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closeNoteModal();
            }
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-md mx-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingUser?.note ? '编辑备注' : '添加备注'}
              </h3>
              <button
                onClick={closeNoteModal}
                className="text-gray-400 hover:text-gray-600 text-xl"
              >
                ×
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                用户邮箱: {editingUser?.email}
              </label>
              <textarea
                id="note-textarea"
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                    e.preventDefault();
                    saveNote();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    closeNoteModal();
                  }
                }}
                placeholder="请输入备注内容... (Ctrl+Enter保存, ESC取消)"
                className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                disabled={noteLoading}
                maxLength={500}
              />
              <div className="text-xs text-gray-400 mt-1 text-right">
                {noteContent.length}/500
              </div>
            </div>

            {noteError && (
              <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                {noteError}
              </div>
            )}

            {noteSuccess && (
              <div className="mb-4 text-sm text-green-600 bg-green-50 border border-green-200 rounded-lg p-3 flex items-center">
                <span className="mr-2">✅</span>
                {noteSuccess}
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <Button
                onClick={closeNoteModal}
                variant="outline"
                size="sm"
                disabled={noteLoading}
              >
                取消
              </Button>
              <Button
                onClick={saveNote}
                size="sm"
                disabled={noteLoading || !noteContent.trim() || !!noteSuccess}
                className={noteSuccess ? 'bg-green-600 hover:bg-green-700' : ''}
              >
                {noteLoading ? '保存中...' : noteSuccess ? '已保存' : '保存'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* API Key管理弹窗 */}
      {showApiKeyModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleCloseApiKeyModal();
            }
          }}
        >
          <div 
            className="bg-white rounded-lg w-full max-w-4xl mx-4 shadow-xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">管理我的API Key</h2>
                <button
                  onClick={handleCloseApiKeyModal}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                  disabled={apiKeyModalLoading}
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* 错误信息 */}
              {apiKeyModalError && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="text-red-400 mr-3">⚠️</div>
                    <div>
                      <h3 className="text-sm font-medium text-red-800">操作失败</h3>
                      <p className="mt-1 text-sm text-red-700">{apiKeyModalError}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* 成功信息 */}
              {apiKeyModalSuccess && (
                <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="text-green-400 mr-3">✅</div>
                    <div>
                      <h3 className="text-sm font-medium text-green-800">操作成功</h3>
                      <p className="mt-1 text-sm text-green-700">{apiKeyModalSuccess}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* 添加新API Key表单 */}
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">绑定新的API Key</h3>
                <ApiKeyBindForm
                  onSubmit={handleBindApiKey}
                  loading={apiKeyModalLoading}
                />
              </div>

              {/* 已绑定的API Key列表 */}
              <div className="mt-8">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  已绑定的API Key ({boundApiKeys.length})
                </h3>

                {apiKeyModalLoading && boundApiKeys.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-gray-400 text-2xl mb-2">⏳</div>
                    <p className="text-gray-500">加载中...</p>
                  </div>
                ) : boundApiKeys.length > 0 ? (
                  <div className="space-y-4">
                    {boundApiKeys.map((apiKey, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-3 mb-2">
                              <h4 className="font-medium text-gray-900 truncate">
                                {apiKey.key_name}
                              </h4>
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                apiKey.is_active 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {apiKey.is_active ? '激活' : '未激活'}
                              </span>
                            </div>
                            
                            <div className="text-sm text-gray-600 space-y-1">
                              <div className="flex items-center">
                                <span className="font-mono bg-gray-100 px-2 py-1 rounded text-xs mr-2">
                                  {apiKey.api_key.substring(0, 12)}...{apiKey.api_key.substring(apiKey.api_key.length - 8)}
                                </span>
                                <button 
                                  onClick={() => {
                                    navigator.clipboard.writeText(apiKey.api_key);
                                    alert('API Key已复制到剪贴板！');
                                  }}
                                  className="text-blue-600 hover:text-blue-800 text-xs"
                                >
                                  📋 复制
                                </button>
                              </div>
                              
                              <div className="text-gray-500">
                                <p>系统: {apiKey.system_name} ({apiKey.system_id})</p>
                                <p>绑定时间: {new Date(apiKey.bound_at).toLocaleString('zh-CN')}</p>
                                {apiKey.last_used_at && (
                                  <p>最后使用: {new Date(apiKey.last_used_at).toLocaleString('zh-CN')}</p>
                                )}
                                {apiKey.notes && (
                                  <p>备注: {apiKey.notes}</p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* 删除按钮 */}
                          <div className="ml-4">
                            <Button
                              onClick={() => handleUnbindApiKey(apiKey.api_key, apiKey.key_name)}
                              disabled={apiKeyModalLoading}
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 hover:border-red-300 disabled:opacity-50"
                            >
                              🗑️ 解绑
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-gray-400 text-4xl mb-4">🔑</div>
                    <p className="text-gray-500 mb-2">暂无绑定的API Key</p>
                    <p className="text-sm text-gray-400">
                      在上方表单中添加您的第一个API Key
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};